import { useEffect, useState, useMemo } from "react";
import { IconButton, Card } from "../EditorStyles";
import { FormattedMessage } from "react-intl";
import { LayerGroup } from "../../../types";
import SpinnerWrapper from "../BaseComponents/SpinnerWrapper";
import NoDataNotice from "../BaseComponents/NoDataNotice";
import { useEditState } from "../EditContext";
import { useRestAuth } from "../../../hooks/useRestAuth";
import { useHistory, useLocation } from "react-router-dom";
import InlinePopup from "../BaseComponents/InlinePopup";
import { useAppState } from "../../../AppContext";
import { MenuItemTitle, StyledMenuItem } from "../../Sidebar/MenuItemStyles";
import { makeKeyDownHandler } from "../../../utils/a11y-utils";
import { Content } from "../../Sidebar/SidebarStyles";

const MapPage = () => {
  const [maps, setMaps] = useState<LayerGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { setTarget, target, mapRepository } = useEditState();
  const restAuth = useRestAuth();
  const history = useHistory();
  const location = useLocation();
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const { state } = useAppState();

  useEffect(() => {
    if (location.pathname === "/editor/map" && target) {
      setTarget(undefined);
    }
  }, [location.pathname, setTarget, target]);

  useEffect(() => {
    if (restAuth) {
      setLoading(true);
      const data = state.map.layerGroups;
      setMaps(data);
      setLoading(false);
    }
  }, [mapRepository, restAuth, state.map.layerGroups]);

  const mapItems = useMemo(() => {
    return maps.map((map: LayerGroup) => (
      <StyledMenuItem key={map.id} className={target === map.id ? "active" : ""}>
        <div
          onClick={() => history.push(`/editor/map/${map.id}/${map.scenarios[0].slug}`)}
          onKeyDown={makeKeyDownHandler(() =>
            history.push(`/editor/map/${map.id}/${map.scenarios[0].slug}`)
          )}
          role="button"
          tabIndex={0}
        >
          <MenuItemTitle>{map.title}</MenuItemTitle>
        </div>
      </StyledMenuItem>
    ));
  }, [maps, target, history]);

  return (
    <div style={{ overflowY: "auto" }}>
        <Content>
          <Card style={{ marginBottom: "22px" }}>
            <div>
              <StyledMenuItem className={target === undefined ? "active" : ""}>
                <div
                  onClick={() => history.push(`/editor/map`)}
                  onKeyDown={makeKeyDownHandler(() => history.push(`/editor/map`))}
                  role="button"
                  tabIndex={0}
                >
                  <MenuItemTitle>No layers (global map)</MenuItemTitle>
                </div>
              </StyledMenuItem>
            </div>
          </Card>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h2>Layers</h2>
            <IconButton onClick={() => setShowHelpPopup(true)}>
              <div>?</div>
            </IconButton>
            {showHelpPopup && (
              <InlinePopup onClose={() => setShowHelpPopup(false)}>
                <h3>
                  <FormattedMessage
                    id="editor.maps.help.title"
                    defaultMessage="Manual: managing the map"
                    description="Title for map help popup"
                  />
                </h3>
                <p>
                  <FormattedMessage
                    id="editor.maps.help.p1"
                    defaultMessage="The general map contains all links to panoramic photos and POI placed as points on the map."
                    description="Paragraph 1 for map help popup"
                  />
                </p>
                <p>
                  <FormattedMessage
                    id="editor.maps.help.p2"
                    defaultMessage="These points can be edited and deleted. Points located on the general map are not visible by default on maps in the Stakeholder Engagement Platform or the Online Visitor Centre."
                    description="Paragraph 2 for map help popup"
                  />
                </p>
                <p>
                  <FormattedMessage
                    id="editor.maps.help.p3"
                    defaultMessage="A specific map layer can be managed by clicking on the map layer in the overview."
                    description="Paragraph 3 for map help popup"
                  />
                </p>
                <p>
                  <FormattedMessage
                    id="editor.maps.help.p4"
                    defaultMessage="By clicking on a point on the detail page of a map layer, that point can be turned on or off for that map layer."
                    description="Paragraph 4 for map help popup"
                  />
                </p>
                <p>
                  <FormattedMessage
                    id="editor.maps.help.p5"
                    defaultMessage="Any point that is turned on on a map layer will be shown on that map layer in the Stakeholder Engagement Platform or the Online Visitor Centre."
                    description="Paragraph 5 for map help popup"
                  />
                </p>
              </InlinePopup>
            )}
          </div>
          <Card>
            <SpinnerWrapper loading={loading}>
              {maps.length === 0 ? <NoDataNotice /> : <div>{mapItems}</div>}
            </SpinnerWrapper>
          </Card>
        </Content>
      </div>
  );
};

export default MapPage;
